import React, { ChangeEvent, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import Webcam from "react-webcam";

interface CameraProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => Promise<void>;
  handleImageChange: (
    input: File | ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
}

export const Camera: React.FC<CameraProps> = ({
  open,
  onClose,
  onCapture,
  handleImageChange,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [facingMode, setFacingMode] = React.useState<"user" | "environment">(
    "environment",
  );

  const handleSwitchCamera = () => {
    setFacingMode(prev => (prev === "user" ? "environment" : "user"));
  };

  const handleCapture = async () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        const res = await fetch(imageSrc);
        const blob = await res.blob();
        const file = new File([blob], `file.jpg`, {
          type: "image/jpeg",
        });
        await onCapture(file);
        onClose();
      }
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const renamedFile = new File(
        [file],
        `file.${file.name.split(".").pop()}`,
        { type: file.type },
      );
      // Crie um evento fake para manter compatibilidade com handleImageChange
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(renamedFile);
      const fakeEvent = {
        ...e,
        target: { ...e.target, files: dataTransfer.files },
      } as ChangeEvent<HTMLInputElement>;
      await handleImageChange(fakeEvent);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiDialog-container": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        },
      }}
    >
      <DialogTitle>
        Capturar Imagem
        <IconButton
          aria-label="fechar"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={{
            facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          }}
          style={{
            width: "100%",
            borderRadius: 8,
            marginBottom: 16,
            background: "#000",
            transform: "none",
          }}
        />
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          <IconButton
            color="primary"
            onClick={handleSwitchCamera}
            aria-label="Trocar câmera"
          >
            <CameraswitchIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={handleCapture}
            aria-label="Capturar"
          >
            <PhotoCameraIcon />
          </IconButton>
          <label htmlFor="upload-image">
            <input
              accept="image/*"
              id="upload-image"
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <IconButton color="primary" component="span" aria-label="Upload">
              <UploadFileIcon />
            </IconButton>
          </label>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
